import AdminChargeSelect from "../../../common/AdminChargeSelect";

export const INP =
  "h-8 w-full min-w-0 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none";

export function PricingModeSwitch({ mode, onChange }) {
  return (
    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
      {["full", "pax"].map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`rounded-md px-3 py-1 text-xs font-semibold capitalize transition ${
            mode === m ? "bg-white text-primary shadow-sm" : "text-gray-600"
          }`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

export function CollapsibleBlock({ title, subtitle, open, onToggle, actions, children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-2 bg-gray-50/80 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-80"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
            {subtitle ? <p className="truncate text-xs text-gray-500">{subtitle}</p> : null}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          <button type="button" onClick={onToggle} className="p-1 text-gray-400 hover:text-gray-600">
            <i className={`fi fi-rr-angle-small-${open ? "up" : "down"}`} />
          </button>
        </div>
      </div>
      {open ? <div className="border-t border-gray-100 p-2 sm:p-3">{children}</div> : null}
    </div>
  );
}

export function PriceTable({ columns, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left">
            {columns.map((col) => (
              <th
                key={col}
                className="pb-1.5 pr-2 text-[10px] font-bold uppercase tracking-wide text-gray-500"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">{children}</tbody>
      </table>
    </div>
  );
}

export function SlotPriceTable({ slotId, tickets, pricingMode, getSlotTicketRow, setSlotTicketField }) {
  const cols =
    pricingMode === "pax"
      ? ["Ticket", "Adult ₹", "Child ₹"]
      : ["Ticket", "Full ₹"];

  return (
    <PriceTable columns={cols}>
      {tickets.map((t) => {
        const sp = getSlotTicketRow(slotId, t.id);
        return (
          <tr key={t.id}>
            <td className="py-1.5 pr-2 font-medium text-gray-800">{t.name}</td>
            <td className="py-1.5 pr-2">
              <input
                type="number"
                min="0"
                placeholder="0"
                value={sp.price ?? ""}
                onChange={(e) => setSlotTicketField(slotId, t.id, "price", e.target.value)}
                className={INP}
              />
            </td>
            {pricingMode === "pax" && (
              <td className="py-1.5">
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={sp.childPrice ?? ""}
                  onChange={(e) => setSlotTicketField(slotId, t.id, "childPrice", e.target.value)}
                  className={INP}
                />
              </td>
            )}
          </tr>
        );
      })}
    </PriceTable>
  );
}

export function TicketPriceTable({ ticket, pricingMode, onTicketChange }) {
  const cols =
    pricingMode === "pax"
      ? ["Adult ₹", "Child ₹", "Discount %", "Admin %", "Max / user"]
      : ["Full ₹", "Discount %", "Admin %", "Max / user"];

  return (
    <PriceTable columns={cols}>
      <tr>
        {pricingMode === "pax" ? (
          <>
            <td className="py-1.5 pr-2">
              <input
                type="number"
                min="0"
                value={ticket.price ?? ""}
                onChange={(e) => onTicketChange(ticket.id, "price", e.target.value)}
                className={INP}
              />
            </td>
            <td className="py-1.5 pr-2">
              <input
                type="number"
                min="0"
                value={ticket.childPrice ?? ""}
                onChange={(e) => onTicketChange(ticket.id, "childPrice", e.target.value)}
                className={INP}
              />
            </td>
          </>
        ) : (
          <td className="py-1.5 pr-2">
            <input
              type="number"
              min="0"
              value={ticket.price ?? ""}
              onChange={(e) => onTicketChange(ticket.id, "price", e.target.value)}
              className={INP}
            />
          </td>
        )}
        <td className="py-1.5 pr-2">
          <input
            type="number"
            min="0"
            max="100"
            value={ticket.discount ?? ""}
            onChange={(e) =>
              onTicketChange(
                ticket.id,
                "discount",
                e.target.value === "" ? "" : Math.min(100, Number(e.target.value))
              )
            }
            className={INP}
          />
        </td>
        <td className="py-1.5 pr-2">
          <AdminChargeSelect
            value={ticket.adminCharge ?? ""}
            onChange={(v) => onTicketChange(ticket.id, "adminCharge", v)}
            className="h-8 py-0"
          />
        </td>
        <td className="py-1.5">
          <input
            type="number"
            min="0"
            value={ticket.maximumAllowedBookingsPerUser ?? ""}
            onChange={(e) =>
              onTicketChange(ticket.id, "maximumAllowedBookingsPerUser", e.target.value)
            }
            className={INP}
          />
        </td>
      </tr>
    </PriceTable>
  );
}

export function SeasonTicketTable({ season, tickets, pricingMode, onUpdate }) {
  const cols =
    pricingMode === "pax" ? ["Ticket", "Adult ₹", "Child ₹"] : ["Ticket", "Full ₹"];

  return (
    <PriceTable columns={cols}>
      {tickets.map((t) => (
        <tr key={t.id}>
          <td className="py-1.5 pr-2 font-medium text-gray-800">{t.type}</td>
          <td className="py-1.5 pr-2">
            <input
              type="number"
              value={t.price ?? ""}
              onChange={(e) => onUpdate(season.id, t.id, "price", e.target.value)}
              placeholder="Default"
              className={INP}
            />
          </td>
          {pricingMode === "pax" && (
            <td className="py-1.5">
              <input
                type="number"
                value={t.childPrice ?? ""}
                onChange={(e) => onUpdate(season.id, t.id, "childPrice", e.target.value)}
                placeholder="Child"
                className={INP}
              />
            </td>
          )}
        </tr>
      ))}
    </PriceTable>
  );
}

export function SeasonSlotPriceTable({
  season,
  slot,
  tickets,
  pricingMode,
  getSlotPrice,
  onSlotPriceChange,
}) {
  const cols =
    pricingMode === "pax" ? ["Ticket", "Adult ₹", "Child ₹"] : ["Ticket", "Full ₹"];

  return (
    <PriceTable columns={cols}>
      {tickets.map((st) => {
        const sp = getSlotPrice(st, slot.id);
        return (
          <tr key={st.id}>
            <td className="py-1.5 pr-2 font-medium text-gray-800">{st.type}</td>
            <td className="py-1.5 pr-2">
              <input
                type="number"
                min="0"
                value={sp.price ?? ""}
                onChange={(e) =>
                  onSlotPriceChange(season.id, st.id, slot.id, "price", e.target.value)
                }
                className={INP}
              />
            </td>
            {pricingMode === "pax" && (
              <td className="py-1.5">
                <input
                  type="number"
                  min="0"
                  value={sp.childPrice ?? ""}
                  onChange={(e) =>
                    onSlotPriceChange(season.id, st.id, slot.id, "childPrice", e.target.value)
                  }
                  className={INP}
                />
              </td>
            )}
          </tr>
        );
      })}
    </PriceTable>
  );
}
